import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MagnifyingGlassIcon,
  TrashIcon,
  ShieldExclamationIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { User } from '@tvia-ai/shared';

interface UserWithStats extends User {
  stats?: {
    lawsuitCount: number;
    paidLawsuits: number;
  };
}

const UserManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<UserWithStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        search: search,
      });

      const response = await axios.get<{
        data: UserWithStats[];
        pagination: { total: number; pages: number };
      }>(`/api/admin/users?${params.toString()}`);

      setUsers(response.data.data);
      setTotal(response.data.pagination.total);
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, search]);

  const handleBanUser = async (userId: string, isBanned: boolean) => {
    const reason = isBanned
      ? prompt(t('admin.banReasonPrompt', 'Please enter a reason for banning this user:'))
      : null;

    if (isBanned && !reason) return;

    try {
      await axios.put(`/api/admin/user/${userId}/ban`, { isBanned, reason });
      await fetchUsers();
    } catch (err: any) {
      alert(err.response?.data?.error || t('admin.actionFailed', 'Action failed'));
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm(t('admin.confirmDeleteUser', 'Are you sure you want to delete this user?'))) {
      return;
    }

    try {
      await axios.delete(`/api/admin/user/${userId}`);
      await fetchUsers();
    } catch (err: any) {
      alert(err.response?.data?.error || t('admin.deleteFailed', 'Delete failed'));
    }
  };

  const handleToggleAdmin = async (userId: string, isAdmin: boolean) => {
    try {
      await axios.put(`/api/admin/user/${userId}/admin`, { isAdmin: !isAdmin });
      await fetchUsers();
    } catch (err: any) {
      alert(err.response?.data?.error || t('admin.actionFailed', 'Action failed'));
    }
  };

  if (isLoading && users.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">
          {t('admin.userManagement', 'User Management')}
        </h2>
        <div className="text-sm text-gray-500">
          {t('admin.totalUsers', 'Total Users')}: {total}
        </div>
      </div>

      {/* Search */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder={t('admin.searchUsers', 'Search by name or email...')}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.user', 'User')}
                </th>
                <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.lawsuits', 'Lawsuits')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.status', 'Status')}
                </th>
                <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.role', 'Role')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.actions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {user.firstName} {user.lastName}
                    </div>
                    <div className="text-xs text-gray-500 truncate max-w-[180px]">{user.email}</div>
                    <div className="sm:hidden mt-1 text-xs text-gray-500">
                      {user.stats?.lawsuitCount || 0} {t('admin.lawsuits', 'lawsuits')}
                    </div>
                  </td>
                  <td className="hidden sm:table-cell px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {user.stats?.lawsuitCount || 0} total
                    </div>
                    <div className="text-xs text-gray-500">
                      {user.stats?.paidLawsuits || 0} paid
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {user.isBanned ? (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                        {t('admin.banned', 'Banned')}
                      </span>
                    ) : (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        {t('admin.active', 'Active')}
                      </span>
                    )}
                  </td>
                  <td className="hidden md:table-cell px-4 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleAdmin(user.id, user.isAdmin || false)}
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full cursor-pointer ${
                        user.isAdmin
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {user.isAdmin ? t('admin.admin', 'Admin') : t('admin.user', 'User')}
                    </button>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleBanUser(user.id, !user.isBanned)}
                        className={`${
                          user.isBanned
                            ? 'text-green-600 hover:text-green-900'
                            : 'text-yellow-600 hover:text-yellow-900'
                        }`}
                        title={user.isBanned ? t('admin.unban', 'Unban') : t('admin.ban', 'Ban')}
                      >
                        {user.isBanned ? (
                          <CheckCircleIcon className="h-5 w-5" />
                        ) : (
                          <ShieldExclamationIcon className="h-5 w-5" />
                        )}
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="text-red-600 hover:text-red-900"
                        title={t('admin.delete', 'Delete')}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                    {t('admin.noUsers', 'No users found')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-lg shadow">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            {t('pagination.previous', 'Previous')}
          </button>
          <span className="text-sm text-gray-700 self-center">
            {page} / {Math.ceil(total / limit)}
          </span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page >= Math.ceil(total / limit)}
            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            {t('pagination.next', 'Next')}
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              {t('pagination.showing', 'Showing')}{' '}
              <span className="font-medium">{Math.min((page - 1) * limit + 1, total)}</span>{' '}
              {t('pagination.to', 'to')}{' '}
              <span className="font-medium">{Math.min(page * limit, total)}</span>{' '}
              {t('pagination.of', 'of')}{' '}
              <span className="font-medium">{total}</span>{' '}
              {t('pagination.results', 'results')}
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                {t('pagination.previous', 'Previous')}
              </button>
              <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                {page}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= Math.ceil(total / limit)}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                {t('pagination.next', 'Next')}
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagementPage;
